import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { confirmarEntrega, marcarNoEntregada, uploadEntregaFoto, getEntregaFotoSignedUrl, getTrackingPublico, ESTADO_LABEL, ESTADO_COLOR } from '../lib/entregasApi'

const MOTIVOS_NO_ENTREGA = [
  'No había nadie',
  'No atendió portero',
  'Dirección no encontrada',
  'Cliente rechazó',
  'Otro',
]

export default function Tracking() {
  const { token } = useParams()
  const { user, role, loading: authLoading } = useAuth()
  const [tracking, setTracking] = useState(null)
  const [detalleRepartidor, setDetalleRepartidor] = useState(null)  // datos del cliente (solo logueado)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!token) return
    getTrackingPublico(token).then(t => {
      setTracking(t)
      setLoading(false)
    }).catch(e => {
      console.error(e)
      setLoading(false)
    })
  }, [token])

  // Si está logueado como repartidor/admin → trae datos completos del cliente para verificar
  useEffect(() => {
    if (!token || !user || !(role === 'repartidor' || role === 'admin')) return
    supabase
      .from('entregas')
      .select(`
        bultos_total, notas, productos_extras, kit_codigo,
        cliente:clientes!entregas_cliente_id_fkey(
          barrio, direccion, apto, tipo_vivienda, alias,
          titular:profiles!clientes_titular_fkey(nombre, phone),
          beneficiario:profiles!clientes_beneficiario_fkey(nombre, phone)
        ),
        kit:kits!entregas_kit_codigo_fkey(nombre)
      `)
      .eq('tracking_token', token)
      .maybeSingle()
      .then(async ({ data, error }) => {
        if (error) console.error('detalle repartidor:', error)
        if (data?.kit_codigo) {
          // Sumar regalos del kit (es_regalo=true) para mostrar como hint en el panel
          const { data: regalos } = await supabase
            .from('kit_items')
            .select('cantidad, producto:productos(nombre)')
            .eq('kit_codigo', data.kit_codigo)
            .eq('es_regalo', true)
          data.regalos_del_kit = regalos || []
        } else {
          data && (data.regalos_del_kit = [])
        }
        // Regalos puntuales del extras_jsonb
        const extrasRegalo = (data?.productos_extras || []).filter(x => x?.es_regalo)
        data && (data.regalos_extras = extrasRegalo)
        setDetalleRepartidor(data || null)
      })
  }, [token, user?.id, role])

  if (loading || authLoading) return <div className="loading">Cargando...</div>

  // SEGURIDAD: solo admin/repartidor pueden ver datos de la entrega
  if (!user) {
    return (
      <div className="track-shell">
        <div className="track-card">
          <div className="brand">Lim<em>pack</em></div>
          <div className="lock-icon">🔒</div>
          <h1>Acceso restringido</h1>
          <p>Esta página solo está disponible para el equipo de Limpack. Iniciá sesión para continuar.</p>
          <a href={`/login?next=/d/${token}`} className="btn-primary" style={{ marginTop: 18, display: 'inline-block' }}>Iniciar sesión</a>
          <div className="track-foot">
            ¿Sos cliente y querés saber sobre tu pedido?
            <br />Escribinos al WhatsApp <a href="https://wa.me/59898610001">098 610 001</a>
          </div>
        </div>
        <style>{LOCK_STYLES}</style>
      </div>
    )
  }

  if (user && role !== 'admin' && role !== 'repartidor') {
    return (
      <div className="track-shell">
        <div className="track-card">
          <div className="brand">Lim<em>pack</em></div>
          <div className="lock-icon">🚫</div>
          <h1>Sin acceso</h1>
          <p>Tu usuario no tiene permisos para ver esta entrega. Si necesitás acceso contactá a admin.</p>
          <a href="/" className="btn-primary" style={{ marginTop: 18, display: 'inline-block' }}>Volver al inicio</a>
        </div>
        <style>{LOCK_STYLES}</style>
      </div>
    )
  }

  if (!tracking) {
    return (
      <div className="track-shell">
        <div className="track-card">
          <div className="brand">Lim<em>pack</em></div>
          <h1>No encontramos esta entrega</h1>
          <p>El código no es válido o ya expiró.</p>
          <a href="/" className="btn-primary" style={{ marginTop: 18, display: 'inline-block' }}>Volver al inicio</a>
        </div>
      </div>
    )
  }

  const puedeConfirmar = user && (role === 'repartidor' || role === 'admin')
  const entregaCerrada = ['entregada', 'no_entregada', 'cancelada'].includes(tracking.estado)

  return (
    <div className="track-shell">
      <div className="track-card">
        <div className="brand">Lim<em>pack</em></div>
        <div className="orden-num">{tracking.numero_orden}</div>

        <h1>Tu entrega</h1>

        <Estado tracking={tracking} />

        <div className="track-info">
          <Info label="Kit" value={tracking.kit_nombre || '—'} />
          <Info label="Fecha programada" value={fechaLabel(tracking.fecha_programada)} />
          <Info label="Ventana" value={tracking.ventana === 'tarde' ? '14 a 18hs' : '10 a 14hs'} />
          {tracking.ts_en_ruta && <Info label="Salió a ruta" value={timeLabel(tracking.ts_en_ruta)} />}
          {tracking.ts_entregada && <Info label="Entregado" value={timeLabel(tracking.ts_entregada)} />}
          {tracking.ts_no_entregada && <Info label="Visita fallida" value={timeLabel(tracking.ts_no_entregada)} />}
          {tracking.recibido_por && <Info label="Recibió" value={tracking.recibido_por} />}
        </div>

        {tracking.estado === 'no_entregada' && (
          <NoEntregaInfo tracking={tracking} />
        )}

        {tracking.estado === 'entregada' && tracking.foto_url && (
          <FotoComprobante path={tracking.foto_url} label="Foto del paquete entregado" />
        )}

        {puedeConfirmar && detalleRepartidor && (
          <PanelClienteRepartidor d={detalleRepartidor} />
        )}

        {puedeConfirmar && !entregaCerrada ? (
          <FormRepartidor token={token} onDone={() => getTrackingPublico(token).then(setTracking)} />
        ) : !user ? (
          <div className="track-cta">
            <p>¿Sos el repartidor?</p>
            <a href={`/login?next=/d/${token}`} className="btn-primary">Iniciar sesión para confirmar</a>
          </div>
        ) : null}

        <div className="track-foot">
          <a href="https://limpack.uy">limpack.uy</a> · <a href="https://wa.me/59898610001">WhatsApp</a>
        </div>
      </div>

      <style>{`
        .track-shell {
          min-height: 100vh; padding: 16px;
          background: linear-gradient(180deg, var(--paper) 0%, #fff 100%);
          display: flex; align-items: flex-start; justify-content: center;
        }
        .track-card {
          width: 100%; max-width: 480px;
          background: #fff; border: 1px solid var(--line); border-radius: 16px;
          padding: 28px 24px; box-shadow: 0 8px 24px rgba(15,31,61,.06);
        }
        .track-card .brand { font-size: 24px; font-weight: 900; letter-spacing: -.02em; color: var(--navy-ink); line-height: 1 }
        .track-card .brand em { font-style: normal; color: var(--teal) }
        .orden-num {
          font-family: monospace; font-size: 11px; color: var(--muted);
          margin-top: 4px; margin-bottom: 18px;
        }
        .track-card h1 {
          font-size: 22px; font-weight: 800; color: var(--navy-ink);
          letter-spacing: -.01em; margin-bottom: 16px;
        }
        .estado-pill {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 14px; border-radius: 999px;
          font-weight: 700; font-size: 13px;
          text-transform: uppercase; letter-spacing: .08em;
          margin-bottom: 22px;
        }
        .estado-pill .dot { width: 8px; height: 8px; border-radius: 50% }
        .track-info {
          display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
          margin-bottom: 24px;
        }
        .track-info-item {
          background: var(--paper); border-radius: 10px; padding: 10px 12px;
        }
        .track-info-label { font-size: 10px; color: var(--muted); letter-spacing: .08em; text-transform: uppercase; font-weight: 700 }
        .track-info-value { font-size: 14px; color: var(--ink); font-weight: 600; margin-top: 2px }
        .track-cta {
          background: var(--paper); padding: 16px; border-radius: 10px;
          text-align: center; margin-bottom: 16px;
        }
        .track-cta p { font-size: 14px; color: var(--muted); margin-bottom: 10px }
        .btn-primary {
          display: inline-block; background: var(--navy); color: #fff !important;
          padding: 12px 22px; border-radius: 8px; font-weight: 700;
          text-decoration: none; font-size: 14px;
        }
        .track-foot { text-align: center; margin-top: 18px; padding-top: 18px; border-top: 1px dashed var(--line); font-size: 13px; color: var(--muted) }
        .repartidor-form {
          margin-top: 16px; background: #fff;
          border: 1px solid var(--line); border-radius: 12px; padding: 12px;
        }
        .rep-tabs {
          display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px;
        }
        .rep-tab {
          padding: 12px 8px; border-radius: 8px; font-weight: 800; font-size: 13px;
          background: #ECFDF5; color: #065F46; border: 1.5px solid #A7F3D0;
          cursor: pointer; font-family: inherit;
        }
        .rep-tab-on { background: #10B981; color: #fff; border-color: #10B981 }
        .rep-tab-danger { background: #FEF2F2; color: #991B1B; border-color: #FECACA }
        .rep-tab-on-danger { background: #DC2626; color: #fff; border-color: #DC2626 }
        .rep-subform { padding: 8px 4px 0 }
        .rep-subform h2 { font-size: 16px; font-weight: 800; color: #065F46; margin-bottom: 14px }
        .rep-subform label { display: block; margin-bottom: 14px }
        .rep-subform label b { display: block; font-size: 11px; font-weight: 700; color: #065F46; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 5px }
        .rep-subform input[type=text], .rep-subform input[type=file], .rep-subform textarea {
          width: 100%; padding: 12px; border: 1.5px solid #A7F3D0; border-radius: 8px;
          font-family: inherit; font-size: 16px; background: #fff; color: var(--ink); box-sizing: border-box;
        }
        .rep-subform textarea { resize: vertical; min-height: 56px }
        .rep-subform input:focus, .rep-subform textarea:focus { outline: 0; border-color: #10B981 }
        .rep-subform button {
          width: 100%; background: #10B981; color: #fff; border: 0;
          padding: 14px; border-radius: 8px; font-size: 15px; font-weight: 800;
          cursor: pointer; margin-top: 6px;
        }
        .rep-subform button:disabled { opacity: .5; cursor: wait }
        .rep-subform-danger h2 { color: #991B1B }
        .rep-subform-danger label b { color: #991B1B }
        .rep-subform-danger input, .rep-subform-danger textarea { border-color: #FECACA }
        .rep-subform-danger input:focus, .rep-subform-danger textarea:focus { border-color: #DC2626 }
        .rep-subform-danger button { background: #DC2626 }
        .rep-chips-label { display: block; font-size: 11px; font-weight: 700; color: #991B1B; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 6px }
        .rep-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px }
        .rep-chip {
          padding: 8px 12px; border-radius: 999px; font-size: 13px; font-weight: 700;
          background: #FEF2F2; color: #991B1B; border: 1.5px solid #FECACA;
          cursor: pointer; font-family: inherit;
        }
        .rep-chip-on { background: #DC2626; color: #fff; border-color: #DC2626 }
        .repartidor-err { background: #FEE2E2; color: #991B1B; padding: 10px 12px; border-radius: 6px; font-size: 13px; margin-top: 10px }
        .repartidor-foto-preview { margin-top: 8px; max-width: 100%; max-height: 200px; border-radius: 8px; border: 1px solid var(--line) }
        .no-entrega-card {
          margin: -8px 0 22px; padding: 16px;
          background: linear-gradient(135deg, #FEF2F2 0%, #fff 100%);
          border: 1.5px solid #FECACA; border-radius: 12px;
        }
        .no-entrega-tag { font-size: 11px; font-weight: 800; color: #991B1B; letter-spacing: .12em; text-transform: uppercase; margin-bottom: 6px }
        .no-entrega-motivo { font-size: 14px; font-weight: 700; color: #7F1D1D; margin-bottom: 4px; line-height: 1.4 }
        .no-entrega-hora { font-size: 12px; color: #B45309 }
        .foto-comprobante { margin: -8px 0 22px }
        .foto-comprobante-tag { font-size: 11px; font-weight: 700; color: var(--muted); letter-spacing: .08em; text-transform: uppercase; margin-bottom: 6px }
        .foto-comprobante img { width: 100%; border-radius: 12px; border: 1px solid var(--line) }
      `}</style>
    </div>
  )
}

const LOCK_STYLES = `
  .track-shell {
    min-height: 100vh; padding: 16px;
    background: linear-gradient(180deg, var(--paper) 0%, #fff 100%);
    display: flex; align-items: center; justify-content: center;
  }
  .track-card {
    width: 100%; max-width: 420px;
    background: #fff; border: 1px solid var(--line); border-radius: 16px;
    padding: 32px 28px; box-shadow: 0 8px 24px rgba(15,31,61,.08);
    text-align: center;
  }
  .track-card .brand { font-size: 22px; font-weight: 900; letter-spacing: -.02em; color: var(--navy-ink); line-height: 1; margin-bottom: 18px }
  .track-card .brand em { font-style: normal; color: var(--teal) }
  .lock-icon { font-size: 56px; margin: 12px 0 18px; opacity: .85 }
  .track-card h1 { font-size: 22px; font-weight: 900; color: var(--navy-ink); margin-bottom: 10px; letter-spacing: -.02em }
  .track-card p { font-size: 14px; color: var(--muted); line-height: 1.5; margin-bottom: 6px }
  .btn-primary {
    display: inline-block; background: var(--navy); color: #fff !important;
    padding: 12px 22px; border-radius: 8px; font-weight: 700;
    text-decoration: none; font-size: 14px;
  }
  .track-foot { margin-top: 22px; padding-top: 18px; border-top: 1px dashed var(--line); font-size: 12px; color: var(--muted); line-height: 1.6 }
`

/* Panel destacado solo para repartidor logueado: confirmá que estás en el lugar correcto */
function PanelClienteRepartidor({ d }) {
  const VIVIENDA = { casa: 'CASA', edificio: 'EDIFICIO', oficina: 'OFICINA' }
  // Quien recibe físicamente: beneficiario si existe, sino titular
  const recibe = d.cliente?.beneficiario || d.cliente?.titular
  const titular = d.cliente?.titular
  const esRegalo = d.cliente?.beneficiario && d.cliente?.beneficiario?.nombre !== d.cliente?.titular?.nombre
  const tel = recibe?.phone
  const telWa = tel ? (tel.replace(/[^\d]/g, '').startsWith('598') ? tel.replace(/[^\d]/g, '') : '598' + tel.replace(/[^\d]/g, '').replace(/^0+/, '')) : null
  const waUrl = telWa ? `https://wa.me/${telWa}` : null
  return (
    <div className="rep-panel">
      <div className="rep-panel-tag">📦 ESTÁS ENTREGANDO A</div>
      <div className="rep-panel-nombre">{recibe?.nombre || '—'}</div>
      {esRegalo && (
        <div style={{ fontSize: 12, opacity: .85, marginBottom: 6, fontWeight: 600 }}>
          🎁 Suscripción contratada por <strong>{titular?.nombre}</strong>
        </div>
      )}
      {d.cliente?.alias && (
        <div style={{ fontSize: 11, fontWeight: 800, opacity: .9, marginBottom: 6, letterSpacing: '.05em' }}>
          📍 {d.cliente.alias.toUpperCase()}
        </div>
      )}
      <div className="rep-panel-dir">
        <strong>{d.cliente?.barrio}</strong>
        {d.cliente?.tipo_vivienda && <span className="rep-panel-viv">{VIVIENDA[d.cliente.tipo_vivienda]}</span>}
      </div>
      <div className="rep-panel-calle">📍 {d.cliente?.direccion || '—'}{d.cliente?.apto ? ` · ${d.cliente.apto}` : ''}</div>

      {(d.regalos_del_kit?.length > 0 || d.regalos_extras?.length > 0) && (
        <div style={{
          marginTop: 8, marginBottom: 4, padding: '8px 10px',
          background: 'rgba(251,191,36,.18)', border: '1px solid rgba(251,191,36,.4)',
          borderRadius: 8, fontSize: 12, lineHeight: 1.4,
        }}>
          <div style={{ fontWeight: 800, marginBottom: 2 }}>🎁 INCLUYE REGALO</div>
          {(d.regalos_del_kit || []).map((r, i) => (
            <div key={'rk' + i} style={{ opacity: .95 }}>· {r.producto?.nombre || 'Producto'} ×{r.cantidad}</div>
          ))}
          {(d.regalos_extras || []).map((r, i) => (
            <div key={'re' + i} style={{ opacity: .95 }}>· {r.nombre || r.sku || 'Extra'} ×{r.cantidad || 1}</div>
          ))}
        </div>
      )}

      <div className="rep-panel-row">
        <div className="rep-panel-kit">
          <span className="rep-panel-mini-tag">CONTENIDO</span>
          <span>{d.kit?.nombre || '—'} · {d.bultos_total || 1} bulto{(d.bultos_total || 1) !== 1 ? 's' : ''}</span>
        </div>
        {tel && (
          <a href={waUrl} target="_blank" rel="noopener" className="rep-panel-wa">💬 WA</a>
        )}
      </div>

      {d.notas && (
        <div className="rep-panel-notas">⚠️ {d.notas}</div>
      )}
      <style>{`
        .rep-panel {
          background: linear-gradient(135deg, #0F1F3D 0%, #00A8B5 100%);
          color: #fff; border-radius: 14px;
          padding: 16px 18px; margin-bottom: 18px;
          box-shadow: 0 6px 20px rgba(15,31,61,.25);
        }
        .rep-panel-tag { font-size: 10px; font-weight: 800; letter-spacing: .15em; opacity: .85; margin-bottom: 4px }
        .rep-panel-nombre { font-size: 22px; font-weight: 900; line-height: 1.1; letter-spacing: -.02em; margin-bottom: 6px }
        .rep-panel-dir { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; margin-bottom: 4px }
        .rep-panel-viv { font-size: 9px; font-weight: 800; letter-spacing: .1em; padding: 2px 6px; background: rgba(255,255,255,.25); border-radius: 3px; backdrop-filter: blur(2px) }
        .rep-panel-calle { font-size: 14px; font-weight: 600; line-height: 1.25; margin-bottom: 12px }
        .rep-panel-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,.2) }
        .rep-panel-kit { font-size: 12px; opacity: .95; display: flex; flex-direction: column; gap: 2px }
        .rep-panel-mini-tag { font-size: 9px; font-weight: 800; letter-spacing: .12em; opacity: .7 }
        .rep-panel-wa { background: #25D366; color: #fff; text-decoration: none; padding: 8px 14px; border-radius: 8px; font-weight: 800; font-size: 13px; white-space: nowrap }
        .rep-panel-notas { margin-top: 10px; padding: 8px 12px; background: rgba(245,158,11,.2); border-left: 3px solid #F59E0B; border-radius: 4px; font-size: 13px; line-height: 1.3 }
      `}</style>
    </div>
  )
}

function Info({ label, value }) {
  return (
    <div className="track-info-item">
      <div className="track-info-label">{label}</div>
      <div className="track-info-value">{value}</div>
    </div>
  )
}

function Estado({ tracking }) {
  const c = ESTADO_COLOR[tracking.estado] || ESTADO_COLOR.programada
  return (
    <div className="estado-pill" style={{ background: c.bg, color: c.text }}>
      <span className="dot" style={{ background: c.dot }}></span>
      {ESTADO_LABEL[tracking.estado]}
    </div>
  )
}

function FormRepartidor({ token, onDone }) {
  // Tab activa: 'entregar' (default) | 'no-entregar'
  const [tab, setTab] = useState('entregar')

  return (
    <div className="repartidor-form">
      <div className="rep-tabs">
        <button
          type="button"
          className={`rep-tab ${tab === 'entregar' ? 'rep-tab-on' : ''}`}
          onClick={() => setTab('entregar')}
        >
          ✅ Entregar
        </button>
        <button
          type="button"
          className={`rep-tab rep-tab-danger ${tab === 'no-entregar' ? 'rep-tab-on-danger' : ''}`}
          onClick={() => setTab('no-entregar')}
        >
          ❌ No pude entregar
        </button>
      </div>

      {tab === 'entregar'
        ? <FormEntrega token={token} onDone={onDone} />
        : <FormNoEntrega token={token} onDone={onDone} />}
    </div>
  )
}

function FormEntrega({ token, onDone }) {
  // Hidratamos desde sessionStorage para sobrevivir cuando iOS/Android pausa
  // el navegador al abrir la cámara nativa
  const STORAGE_KEY = `lp_form_${token}`
  const stored = (() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') } catch { return {} }
  })()
  const [nombre, setNombreRaw] = useState(stored.nombre || '')
  const [doc, setDocRaw] = useState(stored.doc || '')
  const [foto, setFoto] = useState(null)
  const [fotoPreview, setFotoPreview] = useState(null)
  const [enviando, setEnviando] = useState(false)
  const [err, setErr] = useState(null)

  const setNombre = (v) => {
    setNombreRaw(v)
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...stored, nombre: v, doc })) } catch {}
  }
  const setDoc = (v) => {
    setDocRaw(v)
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...stored, nombre, doc: v })) } catch {}
  }

  function handleFoto(e) {
    const f = e.target.files?.[0]
    if (!f) return
    setFoto(f)
    setFotoPreview(URL.createObjectURL(f))
  }

  useEffect(() => () => {
    try { sessionStorage.removeItem(STORAGE_KEY) } catch {}
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (!nombre.trim() || !doc.trim()) {
      setErr('Nombre y documento son obligatorios.')
      return
    }
    setEnviando(true)
    try {
      const fotoPath = foto ? await uploadEntregaFoto(token, foto, 'entrega') : null
      await confirmarEntrega({ token, nombre: nombre.trim(), doc: doc.trim(), fotoPath })
      alert('✅ Entrega confirmada · WhatsApp enviado al cliente')
      onDone()
    } catch (e) {
      setErr(e.message || 'Error desconocido')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <form className="rep-subform" onSubmit={handleSubmit}>
      <h2>📦 Confirmar entrega</h2>
      <label>
        <b>Nombre de quien recibe</b>
        <input type="text" required value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Carlos Pérez" autoComplete="off" />
      </label>
      <label>
        <b>Cédula</b>
        <input type="text" required value={doc} onChange={e => setDoc(e.target.value)} placeholder="Ej: 5.123.456-7" autoComplete="off" inputMode="numeric" />
      </label>
      <label>
        <b>Foto del paquete (opcional)</b>
        <input type="file" accept="image/*" capture="environment" onChange={handleFoto} />
      </label>
      {fotoPreview && (
        <div style={{ marginBottom: 12 }}>
          <img src={fotoPreview} alt="paquete" className="repartidor-foto-preview" />
        </div>
      )}
      <button type="submit" disabled={enviando}>
        {enviando ? 'Confirmando...' : '✅ Confirmar entrega'}
      </button>
      {err && <div className="repartidor-err">{err}</div>}
    </form>
  )
}

function FormNoEntrega({ token, onDone }) {
  const [motivoChip, setMotivoChip] = useState(null)
  const [motivoExtra, setMotivoExtra] = useState('')
  const [foto, setFoto] = useState(null)
  const [fotoPreview, setFotoPreview] = useState(null)
  const [enviando, setEnviando] = useState(false)
  const [err, setErr] = useState(null)

  function handleFoto(e) {
    const f = e.target.files?.[0]
    if (!f) return
    setFoto(f)
    setFotoPreview(URL.createObjectURL(f))
  }

  function buildMotivoFinal() {
    // Concatena chip + texto libre opcional. Para "Otro" el texto es obligatorio.
    const chip = motivoChip || ''
    const extra = motivoExtra.trim()
    if (chip === 'Otro') return extra
    if (chip && extra) return `${chip} — ${extra}`
    return chip || extra
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (!motivoChip) { setErr('Elegí un motivo.'); return }
    if (motivoChip === 'Otro' && !motivoExtra.trim()) {
      setErr('Si elegís "Otro" tenés que describir el motivo.')
      return
    }
    if (!foto) { setErr('Sacá una foto como prueba de la visita.'); return }

    setEnviando(true)
    try {
      const fotoPath = await uploadEntregaFoto(token, foto, 'no-entrega')
      await marcarNoEntregada({ token, motivo: buildMotivoFinal(), fotoPath })
      alert('✅ Visita registrada · WhatsApp enviado al cliente para coordinar')
      onDone()
    } catch (e) {
      setErr(e.message || 'Error desconocido')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <form className="rep-subform rep-subform-danger" onSubmit={handleSubmit}>
      <h2>❌ No se pudo entregar</h2>

      <div className="rep-chips-label">Motivo</div>
      <div className="rep-chips">
        {MOTIVOS_NO_ENTREGA.map(m => (
          <button
            type="button"
            key={m}
            className={`rep-chip ${motivoChip === m ? 'rep-chip-on' : ''}`}
            onClick={() => setMotivoChip(m)}
          >
            {m}
          </button>
        ))}
      </div>

      <label>
        <b>{motivoChip === 'Otro' ? 'Describí el motivo' : 'Detalle (opcional)'}</b>
        <textarea
          rows={2}
          value={motivoExtra}
          onChange={e => setMotivoExtra(e.target.value)}
          placeholder={motivoChip === 'Otro' ? 'Ej: portón roto, no se podía entrar' : 'Algo más que sumar al motivo'}
        />
      </label>

      <label>
        <b>Foto de la puerta · OBLIGATORIA</b>
        <input type="file" accept="image/*" capture="environment" onChange={handleFoto} required />
      </label>
      {fotoPreview && (
        <div style={{ marginBottom: 12 }}>
          <img src={fotoPreview} alt="visita" className="repartidor-foto-preview" />
        </div>
      )}

      <button type="submit" disabled={enviando}>
        {enviando ? 'Registrando...' : '❌ Registrar visita fallida'}
      </button>
      {err && <div className="repartidor-err">{err}</div>}
    </form>
  )
}

function fechaLabel(f) {
  if (!f) return '—'
  return new Date(f + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'short', day: 'numeric', month: 'short' })
}

function timeLabel(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString('es-UY', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })
}

/* Bloque rojo informativo cuando el estado es no_entregada (visible para admin/repartidor logueado) */
function NoEntregaInfo({ tracking }) {
  return (
    <div className="no-entrega-card">
      <div className="no-entrega-tag">❌ No se pudo entregar</div>
      <div className="no-entrega-motivo">{tracking.motivo_no_entrega || 'Sin motivo registrado'}</div>
      {tracking.ts_no_entregada && (
        <div className="no-entrega-hora">Pasamos a las {timeLabel(tracking.ts_no_entregada)}</div>
      )}
      {tracking.foto_url && (
        <div style={{ marginTop: 10 }}>
          <FotoComprobante path={tracking.foto_url} compact />
        </div>
      )}
    </div>
  )
}

/* Renderiza una foto del bucket privado generando signedUrl on-mount */
function FotoComprobante({ path, label, compact }) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    let cancel = false
    getEntregaFotoSignedUrl(path).then(u => { if (!cancel) setUrl(u) })
    return () => { cancel = true }
  }, [path])
  if (!url) return null
  return (
    <div className="foto-comprobante">
      {label && !compact && <div className="foto-comprobante-tag">{label}</div>}
      <a href={url} target="_blank" rel="noopener">
        <img src={url} alt={label || 'Foto comprobante'} />
      </a>
    </div>
  )
}
